"use client";

import React from "react";
import {
    Envelope,
    ChatCircleText,
    Phone,
    Gauge,
    ChartPieSlice,
    Users,
    Calendar,
    Bell,
    Activity,
    Check
} from '@/app/components/Icons/Phosphoricons';
import GetStartedButton from "@/app/components/Utils/GetStartedButton";

const channelFeatures = {
    email: [
        "Personalized content based on customer data",
        "Automated flows for abandoned carts and follow ups",
        "A/B testing for subject lines and content",
        "Advanced delivery optimization",
        "Rich analytics and conversion tracking"
    ],
    text: [
        "Two way messaging capabilities",
        "Custom message templates",
        "Scheduled campaign delivery",
        "Media rich SMS capabilities",
        "Automated responses and sequences"
    ],
    voice: [
        "Natural sounding text to speech",
        "Interactive response options",
        "Appointment reminders",
        "Feedback collection",
        "Call transcription and analysis"
    ]
};

const marketingCapabilities = [
    {
        title: "Customer Journey Orchestration",
        description: "Create seamless customer experiences across touchpoints with intelligent journey mapping and automation",
        icon: Activity,
        color: "bg-purple-700"
    },
    {
        title: "Audience Segmentation",
        description: "Target the right customers with the right message using powerful segmentation capabilities",
        icon: Users,
        color: "bg-blue-700"
    },
    {
        title: "Campaign Management",
        description: "Plan, execute, and monitor multichannel campaigns from a unified dashboard",
        icon: Calendar,
        color: "bg-emerald-800" // Changed from emerald-600 to emerald-800 for better contrast
    },
    {
        title: "Performance Analytics",
        description: "Gain insights into campaign performance with comprehensive reporting and analytics",
        icon: ChartPieSlice,
        color: "bg-amber-800" // Changed from amber-600 to amber-800 for better contrast
    },
    {
        title: "Real Time Notifications",
        description: "Send timely alerts and notifications based on customer actions or system events",
        icon: Bell,
        color: "bg-rose-700"
    },
    {
        title: "Delivery Optimization",
        description: "Maximize engagement with intelligent send time optimization and channel preferences",
        icon: Gauge,
        color: "bg-indigo-700"
    }
];

export default function MarketingNotifications() {
    return (
        <div className="bg-white">
            {/* Hero Section */}
            <div className="relative bg-white overflow-hidden">
                <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl font-system">
                            Multichannel Marketing & Notifications
                        </h1>
                        <p className="mt-6 text-lg text-gray-600 font-system">
                            Connect with your audience through their preferred channels. Deliver personalized messages that drive engagement, conversions, and loyalty.
                        </p>
                        <div className="mt-10 flex justify-center">
                            <GetStartedButton />
                        </div>
                    </div>
                </div>
            </div>

            {/* Communication Channels Section */}
            <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
                <div className="mx-auto max-w-2xl sm:text-center">
                    <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl font-system">
                        Reach Customers on Every Channel
                    </h2>
                    <p className="mt-6 text-lg text-gray-600 font-system">
                        Deliver consistent messaging across email, text, and voice to create unified customer experiences.
                    </p>
                </div>

                <div className="mt-20 lg:grid lg:grid-cols-3 lg:gap-8">
                    {/* Email Channel */}
                    <div className="relative">
                        <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-blue-700 text-white">
                            <Envelope className="h-8 w-8" weight="bold" />
                        </div>
                        <h3 className="mt-6 text-xl font-bold text-gray-900 font-system">Smart Email Platform</h3>
                        <p className="mt-2 text-base text-gray-600 font-system">
                            Create beautiful, responsive emails that drive engagement and convert customers.
                        </p>
                        <ul className="mt-6 space-y-3">
                            {channelFeatures.email.map((feature) => (
                                <li key={feature} className="flex">
                                    <Check className="h-6 w-6 flex-shrink-0 text-blue-700" weight="bold" />
                                    <span className="ml-3 text-sm text-gray-600 font-system">{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Text Channel */}
                    <div className="relative mt-10 lg:mt-0">
                        <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-blue-600 text-white">
                            <ChatCircleText className="h-8 w-8" weight="bold" />
                        </div>
                        <h3 className="mt-6 text-xl font-bold text-gray-900 font-system">Text Messaging System</h3>
                        <p className="mt-2 text-base text-gray-600 font-system">
                            Engage customers with timely, conversational text messages that feel personal.
                        </p>
                        <ul className="mt-6 space-y-3">
                            {channelFeatures.text.map((feature) => (
                                <li key={feature} className="flex">
                                    <Check className="h-6 w-6 flex-shrink-0 text-blue-600" weight="bold" />
                                    <span className="ml-3 text-sm text-gray-600 font-system">{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Voice Channel */}
                    <div className="relative mt-10 lg:mt-0">
                        <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-blue-600 text-white">
                            <Phone className="h-8 w-8" weight="bold" />
                        </div>
                        <h3 className="mt-6 text-xl font-bold text-gray-900 font-system">Voice Notification Hub</h3>
                        <p className="mt-2 text-base text-gray-600 font-system">
                            Deliver important messages through automated voice calls that feel personal and human.
                        </p>
                        <ul className="mt-6 space-y-3">
                            {channelFeatures.voice.map((feature) => (
                                <li key={feature} className="flex">
                                    <Check className="h-6 w-6 flex-shrink-0 text-blue-600" weight="bold" />
                                    <span className="ml-3 text-sm text-gray-600 font-system">{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Marketing Capabilities */}
            <div className="bg-gray-50 py-24 sm:py-32">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl font-system">
                            Powerful Marketing Capabilities
                        </h2>
                        <p className="mt-6 text-lg text-gray-600 font-system">
                            Everything you need to create, automate, and optimize your marketing campaigns.
                        </p>
                    </div>

                    <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-6 sm:mt-20 lg:max-w-none lg:grid-cols-3">
                        {marketingCapabilities.map((capability) => (
                            <div key={capability.title} className="flex flex-col overflow-hidden rounded-lg shadow-lg">
                                <div className={`${capability.color} px-6 py-8`}>
                                    <div className="flex justify-center">
                                        <capability.icon className="h-8 w-8 text-white" weight="bold" />
                                    </div>
                                    <div className="mt-4">
                                        <h3 className="text-lg font-semibold text-white font-system">
                                            {capability.title}
                                        </h3>
                                    </div>
                                </div>
                                <div className="flex flex-1 flex-col justify-between bg-white p-6">
                                    <p className="text-sm text-gray-600 font-system">{capability.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="bg-white">
                <div className="mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:flex lg:items-center lg:justify-between lg:px-8">
                    <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl font-system">
                        Ready to enhance your customer communications?
                        <span className="block text-lg font-normal mt-2">Get started today.</span>
                    </h2>
                    <div className="mt-10 flex items-center gap-x-6 lg:mt-0 lg:flex-shrink-0">
                        <GetStartedButton />
                    </div>
                </div>
            </div>
        </div>
    );
}